/**
 * Route saga for /admin/dependencies: checks every registry in the manifest (plus a
 * pinned `redactionStatusesSchemaId`, when set) against the live API, then re-runs on
 * RUN_DEPENDENCY_CHECKS. Rows report as they land; one row failing never stops the rest.
 */
import {
  all,
  call,
  put,
  putResolve,
  race,
  select,
  take,
  takeLatest,
  delay,
} from 'typed-redux-saga/macro';
import { modules } from '@veritone/glc-redux';
import { selectFeatureFlags } from '@common/user-permissions';
import { selectIsApplicationConfigsLoaded } from '@common-modules/app';
import {
  FETCH_APPLICATION_CONFIGS_FAILURE,
  FETCH_APPLICATION_CONFIGS_SUCCESS,
} from '@common-modules/app/actions';
import {
  DEPENDENCY_CHECK_RESULT,
  DEPENDENCY_CHECKS_FINISHED,
  DEPENDENCY_CHECKS_STARTED,
  RUN_DEPENDENCY_CHECKS,
} from './actions';
import { REGISTRY_DEPENDENCIES, resolveRegistryId } from './manifest';
import { DependencyCheckResult } from './models';
import {
  CheckDataRegistryResponse,
  CheckSchemaResponse,
  checkDataRegistry,
  checkSchema,
  classifyRegistryResponse,
  classifySchemaResponse,
} from './services';

const {
  config: { getConfig },
} = modules;

/** Long enough for a slow org config, short enough that a stuck one doesn't hang the page. */
export const APP_CONFIGS_WAIT_MS = 10_000;

const errorMessage = (e: unknown) =>
  e instanceof Error ? e.message : String(e);

/**
 * Org application configs can flip feature flags, so wait for them before deciding what
 * is gated off. A deep link lands here before they finish loading.
 */
function* waitForApplicationConfigs() {
  if (yield* select(selectIsApplicationConfigsLoaded)) {
    return;
  }
  yield* race({
    ok: take(FETCH_APPLICATION_CONFIGS_SUCCESS.type),
    failed: take(FETCH_APPLICATION_CONFIGS_FAILURE.type),
    timeout: delay(APP_CONFIGS_WAIT_MS),
  });
}

/** Rows for a run, with the ones that need no request already resolved. */
export function* buildInitialRows() {
  const config = yield* select(getConfig<Window['config']>);
  const flags = (yield* select(selectFeatureFlags)) as Record<string, unknown>;

  const rows: DependencyCheckResult[] = REGISTRY_DEPENDENCIES.map((dep) => {
    const { id, source } = resolveRegistryId(dep, config);
    const base = {
      configKey: dep.configKey,
      kind: 'registry' as const,
      id,
      idSource: source,
      notInSchemasPackage: dep.notInSchemasPackage,
    };
    if (dep.featureFlag && !flags[dep.featureFlag]) {
      return { ...base, status: 'skipped', detail: dep.featureFlag };
    }
    if (dep.emptyStringDisables && config[dep.configKey] === '') {
      return {
        ...base,
        id: undefined,
        idSource: undefined,
        status: 'skipped',
        detail: `${dep.configKey}: ''`,
      };
    }
    return { ...base, status: id ? 'pending' : 'missingConfig' };
  });

  if (config.redactionStatusesSchemaId) {
    rows.push({
      configKey: 'redactionStatusesSchemaId',
      kind: 'pinnedSchema',
      id: config.redactionStatusesSchemaId,
      idSource: 'config',
      status: 'pending',
    });
  }
  return rows;
}

export function* checkRow(row: DependencyCheckResult) {
  if (row.status !== 'pending' || !row.id) {
    return;
  }
  try {
    const outcome =
      row.kind === 'registry'
        ? classifyRegistryResponse(
            (yield* putResolve(checkDataRegistry(row.id) as never)) as
              | CheckDataRegistryResponse
              | undefined
          )
        : classifySchemaResponse(
            (yield* putResolve(checkSchema(row.id) as never)) as
              | CheckSchemaResponse
              | undefined
          );
    yield* put(DEPENDENCY_CHECK_RESULT({ ...row, ...outcome }));
  } catch (e) {
    yield* put(
      DEPENDENCY_CHECK_RESULT({
        ...row,
        status: 'error',
        detail: errorMessage(e),
      })
    );
  }
}

export function* runDependencyChecks() {
  yield* call(waitForApplicationConfigs);
  const rows = yield* call(buildInitialRows);
  yield* put(
    DEPENDENCY_CHECKS_STARTED({
      startedAt: new Date().toISOString(),
      results: rows,
    })
  );
  yield* all(rows.map((row) => call(checkRow, row)));
  yield* put(DEPENDENCY_CHECKS_FINISHED());
}

/** Route saga: one run on entry, then one per Re-run click (latest wins). */
export function* initAdminDependenciesPage() {
  yield* all([
    call(runDependencyChecks),
    takeLatest(RUN_DEPENDENCY_CHECKS.type, runDependencyChecks),
  ]);
}
